import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Medal, Search, Download, FileText, Users, ChevronLeft, ChevronRight } from "lucide-react";

const TIER_COLORS = {
  Diamond: "#8b5cf6", Platinum: "#06b6d4", Gold: "#f59e0b",
  Silver: "#94a3b8", Aspiring: "#64748b",
};

export default function AffiliatesTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [payoutMonth, setPayoutMonth] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [payouts, setPayouts] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const limit = 25;
  const lastPage = Math.max(1, Math.ceil(total / limit));

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/affiliates?page=${page}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setItems(r.data.affiliates || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load affiliates"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, q]);

  const loadPayouts = async () => {
    setPayoutLoading(true);
    try {
      const r = await api.get(`/admin/affiliates/payouts?month=${payoutMonth}`);
      setPayouts(r.data);
      toast.success(`Loaded ${r.data.totals.affiliates_with_bookings} affiliates with bookings in ${payoutMonth}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load payouts"); }
    finally { setPayoutLoading(false); }
  };

  const downloadPayoutsCsv = () => {
    if (!payouts || !payouts.rows.length) { toast.error("No data to export — load a month first"); return; }
    const rows = [
      ["Name", "Email", "AFF Code", "Slug", "Bookings", "GMV (₹)", "Commission (₹)"],
      ...payouts.rows.map((r) => [r.name, r.email, r.code, r.slug, r.bookings, r.gmv, r.commission]),
      [],
      ["TOTAL", "", "", "", payouts.totals.affiliates_with_bookings, payouts.totals.total_gmv, payouts.totals.total_commission],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `safarsetu_affiliate_payouts_${payoutMonth}.csv`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
  };

  return (
    <div data-testid="admin-affiliates-tab">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-[#1A231C] font-['Outfit']">Affiliates &amp; Influencers</h2>
          <p className="text-sm text-[#8A998E]">{total} affiliates · tier resets monthly · payouts via CSV export</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A998E]" />
          <Input className="pl-9 h-9 bg-white border-[#E5E3DB] rounded-full text-sm w-64"
            placeholder="Search name / email / code / slug" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            data-testid="affiliates-search" />
        </div>
      </div>

      {/* Payouts panel */}
      <div className="bg-[#F3F2EE] border border-[#E5E3DB] rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <FileText className="w-4 h-4 text-[#2C5F2D]" />
          <strong className="text-sm">Monthly payouts</strong>
          <span className="text-xs text-[#8A998E]">— commissions earned by affiliates</span>
          <div className="flex-1" />
          <Input type="month" className="bg-white border-[#E5E3DB] rounded-xl text-xs h-8 w-36"
            value={payoutMonth} onChange={(e) => setPayoutMonth(e.target.value)} data-testid="payout-month-input" />
          <Button size="sm" className="rounded-full bg-[#2C5F2D] hover:bg-[#234B24] text-white" onClick={loadPayouts}
            disabled={payoutLoading} data-testid="load-payouts-btn">
            {payoutLoading ? "Loading…" : "Load"}
          </Button>
          {payouts && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={downloadPayoutsCsv}
              data-testid="dl-payouts-csv">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
          )}
        </div>
        {payouts && (
          <div className="mt-3" data-testid="payouts-result">
            <div className="grid grid-cols-3 gap-2 text-xs mb-3">
              <div className="bg-white rounded p-2"><div className="text-[#8A998E]">Affiliates with bookings</div>
                <strong className="text-base">{payouts.totals.affiliates_with_bookings}</strong></div>
              <div className="bg-white rounded p-2"><div className="text-[#8A998E]">Total GMV</div>
                <strong className="text-base">₹{payouts.totals.total_gmv.toLocaleString("en-IN")}</strong></div>
              <div className="bg-white rounded p-2"><div className="text-[#8A998E]">Total commission to pay</div>
                <strong className="text-base text-[#2C5F2D]">₹{payouts.totals.total_commission.toLocaleString("en-IN")}</strong></div>
            </div>
            <div className="overflow-x-auto bg-white rounded-lg">
              <table className="text-xs w-full">
                <thead className="bg-[#F3F2EE] border-b border-[#E5E3DB]">
                  <tr>
                    <th className="text-left p-2">Affiliate</th>
                    <th className="text-left p-2">Code</th>
                    <th className="text-right p-2">Bookings</th>
                    <th className="text-right p-2">GMV</th>
                    <th className="text-right p-2">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.rows.map((r) => (
                    <tr key={r.code} className="border-b border-[#E5E3DB]" data-testid={`payout-row-${r.code}`}>
                      <td className="p-2"><strong>{r.name}</strong><br /><span className="text-[10px] text-[#8A998E]">{r.email}</span></td>
                      <td className="p-2 font-mono text-[10px]">{r.code}</td>
                      <td className="p-2 text-right">{r.bookings}</td>
                      <td className="p-2 text-right">₹{r.gmv.toLocaleString("en-IN")}</td>
                      <td className="p-2 text-right text-[#2C5F2D] font-semibold">₹{r.commission.toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                  {payouts.rows.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-[#8A998E]">No commissions earned in {payouts.month}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Affiliates list */}
      {loading ? (
        <div className="text-center py-12 text-sm text-[#8A998E]">Loading affiliates…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-[#F3F2EE] rounded-xl">
          <Medal className="w-10 h-10 mx-auto text-[#8A998E] mb-3" />
          <p className="text-sm text-[#445049] font-medium">No affiliates yet</p>
          <p className="text-xs text-[#8A998E] mt-1">Customers can join the program from the customer site `/affiliate` page.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#E5E3DB] rounded-xl">
          <table className="text-sm w-full">
            <thead className="bg-[#F3F2EE] border-b border-[#E5E3DB] text-[#445049]">
              <tr>
                <th className="text-left p-3">Affiliate</th>
                <th className="text-left p-3">Code · Slug</th>
                <th className="text-left p-3">Tier (prev month)</th>
                <th className="text-right p-3">Prev GMV</th>
                <th className="text-right p-3">Lifetime GMV</th>
                <th className="text-right p-3">Earnings</th>
                <th className="text-right p-3">Bookings</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.code} className="border-b border-[#E5E3DB]" data-testid={`affiliate-row-${a.code}`}>
                  <td className="p-3"><strong>{a.name}</strong><br /><span className="text-[10px] text-[#8A998E]">{a.email}</span></td>
                  <td className="p-3 font-mono text-[11px]">
                    {a.code}<br />
                    {a.slug ? <a className="text-[#2C5F2D] hover:underline" href={`https://safarsetu.com/with/${a.slug}`} target="_blank" rel="noreferrer">@{a.slug}</a> : <span className="text-[#8A998E]">no slug</span>}
                  </td>
                  <td className="p-3">
                    <Badge variant="outline" style={{ borderColor: TIER_COLORS[a.tier] || "#64748b", color: TIER_COLORS[a.tier] || "#64748b" }}>
                      <Medal className="w-3 h-3 mr-1" /> {a.tier} · {a.tier_rate}%
                    </Badge>
                  </td>
                  <td className="p-3 text-right">₹{(a.prev_month_gmv || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right">₹{(a.lifetime_gmv || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right text-[#2C5F2D] font-semibold">₹{(a.lifetime_earnings || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right">{a.lifetime_bookings || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {lastPage > 1 && (
            <div className="flex items-center justify-center gap-2 p-3 border-t border-[#E5E3DB] bg-[#F3F2EE]">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                data-testid="aff-prev-page"><ChevronLeft className="w-3.5 h-3.5" /> Prev</Button>
              <span className="text-sm text-[#445049]">Page {page} of {lastPage}</span>
              <Button variant="outline" size="sm" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}
                data-testid="aff-next-page">Next <ChevronRight className="w-3.5 h-3.5" /></Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
